import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Badge,
} from "@mui/material";

import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ChevronLeft,
  AdminPanelSettingsOutlined,
  MilitaryTechOutlined,
  DashboardOutlined,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";

import RadarOutlinedIcon from "@mui/icons-material/RadarOutlined";
import HistoryIcon from "@mui/icons-material/History";
import LinkIcon from "@mui/icons-material/Link";
import StorageIcon from "@mui/icons-material/Storage";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';

import { ROLES } from "../config/roles";
import { FONT_MONO } from "../config/fonts";
import { APP_VERSION } from "../config/version";
import useChatUnread from "../hooks/useChatUnread";

const COPYRIGHT_YEAR = new Date().getFullYear();

// Menu, grouped. `path` is the route (it used to be the label in lower case, which tied the
// wording to the URL); `role` limits an item to that role; a section with `role` is hidden
// entirely for everyone else.
const SECTIONS = [
  {
    title: "Overview",
    items: [{ label: "Dashboard", path: "dashboard", icon: <DashboardOutlined /> }],
  },
  {
    title: "Operations",
    items: [
      { label: "Radar", path: "radar", icon: <RadarOutlinedIcon /> },
      { label: "Review", path: "review", icon: <HistoryIcon /> },
      { label: "Regions", path: "region", icon: <LayersOutlinedIcon /> },
      { label: "SIF Codes", path: "sifcode", icon: <ConfirmationNumberOutlinedIcon /> },
    ],
  },
  {
    title: "Management",
    items: [
      { label: "Users", path: "user", icon: <AdminPanelSettingsOutlined />, role: ROLES.Root },
      { label: "Units", path: "unit", icon: <MilitaryTechOutlined />, role: ROLES.Root },
      { label: "Applications", path: "application", icon: <AppsIcon /> },
      { label: "Connect Log", path: "connectlog", icon: <LinkIcon />, role: ROLES.Root },
    ],
  },
  {
    title: "Communication",
    items: [{ label: "Chat", path: "chat", icon: <ChatOutlinedIcon />, chat: true }],
  },
  {
    title: "SimbaLink VPN",
    role: ROLES.Root,
    items: [
      { label: "VPN Servers", path: "vpnservers", icon: <DnsOutlinedIcon /> },
      { label: "VPN Server IPs", path: "vpnserverips", icon: <StorageIcon /> },
      { label: "VPN Connect Log", path: "vpnconnectlog", icon: <LinkIcon /> },
    ],
  },
];

const Sidebar = ({
  isNonMobile,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  auth,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");

  const navigate = useNavigate();
  const theme = useTheme();
  const paperRef = useRef(null);

  const unread = useChatUnread(Boolean(auth?.accessToken));
  const accent = theme.palette.secondary.main;
  const isDark = theme.palette.mode === "dark";
  const line = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const muted = theme.palette.text.secondary;

  const collapsedWidth = "80px";
  const expandedWidth = drawerWidth;
  const open = isSidebarOpen;   // expanded (desktop) / shown (mobile)

  // --------------------------------------------------
  // Active path
  // --------------------------------------------------
  useEffect(() => {
    setActive(pathname.substring(1).split("/")[0].toLowerCase());
  }, [pathname]);

  // --------------------------------------------------
  // Sidebar transition event (pages listen for the final width)
  // --------------------------------------------------
  useEffect(() => {
    const el = paperRef.current;

    if (!el) return;

    const onTransitionEnd = (ev) => {
      try {
        const prop = ev.propertyName || "";

        if (prop.includes("transform") || prop.includes("width") || prop.includes("left")) {
          const width = el.getBoundingClientRect
            ? Math.round(el.getBoundingClientRect().width)
            : 0;

          window.dispatchEvent(
            new CustomEvent("sidebarToggleComplete", {
              detail: {
                isOpen: isSidebarOpen,
                width,
              },
            })
          );
        }
      } catch (e) {
        // ignore
      }
    };

    el.addEventListener("transitionend", onTransitionEnd);

    // Initial size
    setTimeout(() => {
      try {
        const width = el.getBoundingClientRect
          ? Math.round(el.getBoundingClientRect().width)
          : 0;

        window.dispatchEvent(
          new CustomEvent("sidebarToggleComplete", {
            detail: {
              isOpen: isSidebarOpen,
              width,
            },
          })
        );
      } catch (e) {}
    }, 50);

    return () => {
      el.removeEventListener("transitionend", onTransitionEnd);
    };
  }, [isSidebarOpen]);

  // --------------------------------------------------
  // Navigation
  // --------------------------------------------------
  const handleNavigation = (path) => {
    navigate(`/${path}`);
    setActive(path);
    if (!isNonMobile) setIsSidebarOpen(false);   // phones: close the drawer after picking a page
  };

  const allowed = (role) => !role || auth?.roles?.includes(role);

  // Root manages users and units most: Management comes right after Overview for them
  const orderedSections = auth?.roles?.includes(ROLES.Root)
    ? [
        ...SECTIONS.filter((s) => s.title === "Overview"),
        ...SECTIONS.filter((s) => s.title === "Management"),
        ...SECTIONS.filter((s) => s.title !== "Overview" && s.title !== "Management"),
      ]
    : SECTIONS;

  // One menu row
  const renderItem = ({ label, path, icon, chat }) => {
    const isActive = active === path;

    const showBadge = chat && unread.total > 0;
    const countText = unread.total > 99 ? "99+" : unread.total;
    const badgeText = unread.mentioned ? `@${countText}` : countText;

    const row = (
      <ListItemButton
        key={path}
        onClick={() => handleNavigation(path)}
        aria-current={isActive ? "page" : undefined}
        sx={{
          position: "relative",
          minHeight: 38,
          mx: open ? 1.25 : 1,
          my: "1px",
          px: open ? 1.5 : 0,
          borderRadius: "10px",
          justifyContent: open ? "flex-start" : "center",
          color: isActive ? theme.palette.text.primary : alpha(theme.palette.text.primary, 0.78),
          // Current page: gold gradient fading to the right
          background: isActive
            ? `linear-gradient(90deg, ${alpha(accent, isDark ? 0.26 : 0.28)} 0%, ${alpha(accent, 0.06)} 100%)`
            : "transparent",
          boxShadow: isActive ? `inset 0 0 0 1px ${alpha(accent, 0.22)}` : "none",
          transition: "background-color .2s ease, color .2s ease, padding-left .2s ease",
          "&:hover": {
            backgroundColor: isActive ? alpha(accent, 0.1) : alpha(accent, 0.09),
            color: theme.palette.text.primary,
            // nudge right + bigger icon
            pl: open ? 1.9 : 0,
            "& .MuiListItemIcon-root": {
              color: accent,
              transform: "scale(1.15)",
              filter: `drop-shadow(0 0 6px ${alpha(accent, 0.55)})`,
            },
          },
          // gold bar on the left of the active page
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 8,
            bottom: 8,
            width: 4,
            borderRadius: 2,
            backgroundColor: accent,
            boxShadow: `0 0 10px ${alpha(accent, 0.8)}`,
            opacity: isActive ? 1 : 0,
            transition: "opacity .2s ease",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 1.5 : 0,
            justifyContent: "center",
            // Gold icons: softer for the other pages, full gold for the current page and on hover
            color: isActive ? accent : alpha(accent, 0.75),
            transition: "color .2s ease, transform .2s ease, filter .2s ease",
            // current page: icon glows
            filter: isActive ? `drop-shadow(0 0 6px ${alpha(accent, 0.6)})` : "none",
            "& .MuiSvgIcon-root": { fontSize: "1.4rem" },
          }}
        >
          {showBadge && !open ? (
            <Badge
              badgeContent={badgeText}
              color={unread.mentioned ? "error" : "secondary"}
              overlap="circular"
            >
              {icon}
            </Badge>
          ) : (
            icon
          )}
        </ListItemIcon>

        {open && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              noWrap: true,
              fontSize: "0.94rem",
              fontWeight: isActive ? 800 : 600,
              letterSpacing: "0.01em",
            }}
          />
        )}

        {/* Unread pill (expanded sidebar) */}
        {showBadge && open && (
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.75,
              ml: 1,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: "0.7rem",
              fontWeight: 700,
              backgroundColor: unread.mentioned ? theme.palette.error.main : accent,
              color: unread.mentioned
                ? theme.palette.error.contrastText
                : theme.palette.getContrastText(accent),
            }}
          >
            {badgeText}
          </Box>
        )}
      </ListItemButton>
    );

    // Collapsed: the label shows as a tooltip next to the icon
    return open ? row : (
      <Tooltip key={path} title={label} placement="right" arrow>
        {row}
      </Tooltip>
    );
  };

  const width = isNonMobile ? (open ? expandedWidth : collapsedWidth) : expandedWidth;
  const wideFooter = open || !isNonMobile;   // footer info on one line (not the collapsed rail)

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <>
      {auth && (
        // Not a <nav>: index.css styles every <nav> as a horizontal flex row (for the top navbar)
        <Box>
          <Drawer
            /*
             * Desktop: drawer stays mounted/open; isSidebarOpen = expanded / collapsed width.
             * Mobile: isSidebarOpen = drawer shown / hidden.
             */
            open={isNonMobile ? true : isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            variant="persistent"
            anchor="left"
            PaperProps={{
              ref: paperRef,
            }}
            sx={{
              width,
              flexShrink: 0,

              "& .MuiDrawer-paper": {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.alt,
                boxSizing: "border-box",
                border: "none",
                borderRight: `1px solid ${line}`,
                width,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
            }}
          >
            {/* ================= BRAND ================= */}
            <Box
              sx={{
                flexShrink: 0,
                height: 60,
                px: open ? 2 : 0,
                display: "flex",
                alignItems: "center",
                justifyContent: open ? "flex-start" : "center",
                gap: 1.25,
                borderBottom: `1px solid ${line}`,
              }}
            >
              <Box
                component="img"
                src="/assets/simbalinkicon.png"
                alt="SimbaLink"
                onClick={() => handleNavigation("dashboard")}
                sx={{
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  cursor: "pointer",
                  borderRadius: "10px",
                  boxShadow: `0 0 0 1px ${alpha(accent, 0.35)}`,
                }}
              />

              {open && (
                <Box
                  onClick={() => handleNavigation("dashboard")}
                  sx={{ minWidth: 0, flex: 1, cursor: "pointer", userSelect: "none" }}
                >
                  <Typography
                    noWrap
                    sx={{ fontWeight: 800, fontSize: "1.25rem", letterSpacing: "0.06em", lineHeight: 1.1, color: accent }}
                  >
                    SIMBALINK
                  </Typography>
                </Box>
              )}

              {/* Phones: close the drawer */}
              {!isNonMobile && (
                <IconButton onClick={() => setIsSidebarOpen(false)} aria-label="Close menu" sx={{ color: muted }}>
                  <ChevronLeft />
                </IconButton>
              )}
            </Box>

            {/* ================= NAVIGATION ================= */}
            <Box
              role="navigation"
              aria-label="Main"
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                py: 0.75,
                // Thin scrollbar, only if the window is too short for every menu
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(theme.palette.text.secondary, 0.3), borderRadius: 6 },
                "&::-webkit-scrollbar-thumb:hover": { backgroundColor: alpha(accent, 0.7) },
                "@supports not selector(::-webkit-scrollbar)": {
                  scrollbarWidth: "thin",
                  scrollbarColor: `${alpha(theme.palette.text.secondary, 0.3)} transparent`,
                },
              }}
            >
              {orderedSections.filter((section) => allowed(section.role)).map((section, i) => {
                const items = section.items.filter((item) => allowed(item.role));
                if (items.length === 0) return null;

                return (
                  <Box key={section.title} sx={{ mt: i === 0 ? 0 : 0.5 }}>
                    {open ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.75}
                        // Group label, not a link: light, dim, no icon, with a line after it
                        sx={{ px: 2.75, pt: 1, pb: 0.25, color: alpha(theme.palette.text.secondary, 0.6), userSelect: "none" }}
                      >
                        <Typography sx={{ fontSize: "0.66rem", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                          {section.title}
                        </Typography>
                        <Box flex={1} sx={{ borderTop: `1px solid ${line}`, ml: 0.5 }} />
                      </Box>
                    ) : (
                      // collapsed: a short line between groups instead of the title
                      i > 0 && <Box sx={{ mx: 2.5, my: 0.75, borderTop: `1px solid ${line}` }} />
                    )}

                    <List disablePadding>{items.map(renderItem)}</List>
                  </Box>
                );
              })}
            </Box>

            {/* ================= FOOTER: collapse (desktop) + copyright / version ================= */}
            <Box sx={{ flexShrink: 0, borderTop: `1px solid ${line}` }}>
              {isNonMobile && (
                <Box sx={{ px: 0.75, pt: 0.75 }}>
                  <Tooltip title={open ? "" : "Expand sidebar"} placement="right" arrow>
                    <ListItemButton
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
                      sx={{
                        minHeight: 36,
                        borderRadius: "10px",
                        justifyContent: open ? "flex-start" : "center",
                        px: open ? 1.5 : 0,
                        color: muted,
                        "&:hover": { color: theme.palette.text.primary, backgroundColor: alpha(accent, 0.07) },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 0, color: "inherit", justifyContent: "center" }}>
                        {open ? <KeyboardDoubleArrowLeft fontSize="small" /> : <KeyboardDoubleArrowRight fontSize="small" />}
                      </ListItemIcon>
                      {open && (
                        <ListItemText primary="Collapse" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }} />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </Box>
              )}

              {/* Info line: copyright (year follows the clock) on the left, version on the right.
                  Collapsed rail: both stacked and centred. */}
              <Box
                title={`© Copyright ${COPYRIGHT_YEAR} · Version ${APP_VERSION}`}
                sx={{
                  display: "flex",
                  flexDirection: wideFooter ? "row" : "column",
                  alignItems: "center",
                  justifyContent: wideFooter ? "space-between" : "center",
                  gap: wideFooter ? 1 : 0.25,
                  px: wideFooter ? 2.25 : 0.5,
                  pt: isNonMobile ? 0.5 : 1.25,
                  pb: 1.25,
                  fontSize: wideFooter ? "0.7rem" : "0.6rem",
                  lineHeight: 1.4,
                  // neutral grey, independent of the theme's blue-tinted text colours
                  color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.45)",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}
              >
                <span>{wideFooter ? `© Copyright ${COPYRIGHT_YEAR}` : `© ${COPYRIGHT_YEAR}`}</span>
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT_MONO,
                    letterSpacing: "0.02em",
                    // version a step brighter than the copyright
                    color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.6)",
                  }}
                >
                  v{APP_VERSION}
                </Box>
              </Box>
            </Box>
          </Drawer>
        </Box>
      )}
    </>
  );
};

export default Sidebar;
